import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { get, post } from "../lib/api";
import { COMPANY_STATUSES, RELATIONSHIP_TYPES, humanize } from "../lib/format";
import { Button, Field, Input, Modal, Select, Spinner } from "../ui";
import EntityPicker from "../components/EntityPicker";
import { useShell } from "./context";

const emptyContact = (p = {}) => ({ name: p.name || "", email: "", linkedin_url: "", tier: "", relationship_type: "", company: p.company || null, title: "" });
const emptyCompany = (p = {}) => ({ name: p.name || "", website: "", industry: "", country: "", status: "prospect", linked_crawler_company_id: p.linked_crawler_company_id || "" });

// New person / New company, with a possible-duplicates warning before create
// (GET /v1/crm/duplicates/candidates).
export default function CreateModal() {
    const { createState, closeCreate, toast, bump } = useShell();
    const history = useHistory();
    const kind = createState && createState.kind;
    const [f, setF] = useState({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [existing, setExisting] = useState(null);
    const [candidates, setCandidates] = useState([]);

    useEffect(() => {
        if (!createState) return;
        setF(createState.kind === "company" ? emptyCompany(createState.prefill) : emptyContact(createState.prefill));
        setError(null);
        setExisting(null);
        setCandidates([]);
        setBusy(false);
    }, [createState]);

    if (!createState) return null;
    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v && v.target ? v.target.value : v }));
    const base = kind === "company" ? "/crm/companies/" : "/crm/contacts/";

    const checkDuplicates = async () => {
        if (!f.name || f.name.trim().length < 2) return;
        try {
            const params =
                kind === "company"
                    ? { entity: "company", name: f.name.trim(), domain: f.website || undefined }
                    : { entity: "contact", name: f.name.trim(), email: f.email || undefined, linkedin_url: f.linkedin_url || undefined, company_id: f.company ? f.company.id : undefined };
            const res = await get("v1/crm/duplicates/candidates", params);
            setCandidates(res.data || []);
        } catch (e) {
            setCandidates([]);
        }
    };

    const save = async () => {
        if (!f.name || !f.name.trim()) return setError("Name is required.");
        setBusy(true);
        setError(null);
        setExisting(null);
        try {
            let created;
            if (kind === "company") {
                const body = { name: f.name.trim(), status: f.status };
                ["website", "industry", "country", "linked_crawler_company_id"].forEach((k) => f[k] && (body[k] = f[k].trim()));
                created = await post("v1/crm/companies", body);
            } else {
                const body = { name: f.name.trim() };
                ["email", "linkedin_url", "tier", "relationship_type"].forEach((k) => f[k] && (body[k] = f[k].trim()));
                created = await post("v1/crm/contacts", body);
                if (f.company) {
                    try {
                        await post(`v1/crm/contacts/${created.id}/roles`, { company_id: f.company.id, title: f.title.trim() });
                    } catch (e) {
                        toast(`Saved, but the role was not added: ${e.message}`, { error: true });
                    }
                }
            }
            toast(kind === "company" ? "Company created." : "Person created.");
            bump();
            closeCreate();
            if (!createState.prefill.stay) history.push(base + created.id);
            if (createState.prefill.onCreated) createState.prefill.onCreated(created);
        } catch (e) {
            setBusy(false);
            setError(e.message);
            if (e.status === 409 && e.data.existing_id) setExisting(e.data.existing_id);
        }
    };

    const open = (id) => {
        closeCreate();
        history.push(base + id);
    };

    return (
        <Modal
            open
            title={kind === "company" ? "New company" : "New person"}
            onClose={closeCreate}
            width={480}
            footer={
                <>
                    <Button variant="light" onClick={closeCreate}>Cancel</Button>
                    <Button variant="primary" onClick={save} disabled={busy}>
                        {busy && <Spinner />}Create
                    </Button>
                </>
            }
        >
            <div className="hz-form-grid">
                {error && (
                    <div className="hz-alert hz-alert-danger">
                        {error}
                        {existing && (
                            <>
                                {" "}
                                <button type="button" className="hz-btn-link" onClick={() => open(existing)}>Open the existing record</button>
                            </>
                        )}
                    </div>
                )}
                <Field label="Name" required>
                    <Input value={f.name || ""} onChange={set("name")} onBlur={checkDuplicates} autoFocus />
                </Field>
                {kind === "company" ? (
                    <>
                        <div className="hz-form-2">
                            <Field label="Website">
                                <Input value={f.website} onChange={set("website")} onBlur={checkDuplicates} placeholder="https://" />
                            </Field>
                            <Field label="Industry">
                                <Input value={f.industry} onChange={set("industry")} />
                            </Field>
                        </div>
                        <div className="hz-form-2">
                            <Field label="Country" hint="Two letters, e.g. TR">
                                <Input value={f.country} maxLength={2} onChange={set("country")} />
                            </Field>
                            <Field label="Status">
                                <Select value={f.status} onChange={set("status")} options={COMPANY_STATUSES} />
                            </Field>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="hz-form-2">
                            <Field label="Email">
                                <Input type="email" value={f.email} onChange={set("email")} onBlur={checkDuplicates} />
                            </Field>
                            <Field label="LinkedIn URL">
                                <Input value={f.linkedin_url} onChange={set("linkedin_url")} onBlur={checkDuplicates} placeholder="linkedin.com/in/…" />
                            </Field>
                        </div>
                        <div className="hz-form-2">
                            <Field label="Tier">
                                <Select value={f.tier} onChange={set("tier")} placeholder="None" options={["A", "B", "C"].map((t) => ({ value: t, label: `Tier ${t}` }))} />
                            </Field>
                            <Field label="Relationship">
                                <Select value={f.relationship_type} onChange={set("relationship_type")} placeholder="Not set" options={RELATIONSHIP_TYPES} />
                            </Field>
                        </div>
                        <div className="hz-form-2">
                            <div>
                                <span className="hz-label">Company</span>
                                <EntityPicker kind="company" value={f.company} onChange={set("company")} />
                            </div>
                            <Field label="Title">
                                <Input value={f.title} onChange={set("title")} disabled={!f.company} placeholder={f.company ? "" : "Pick a company first"} />
                            </Field>
                        </div>
                    </>
                )}
                {candidates.length > 0 && (
                    <div className="hz-alert hz-alert-warning">
                        <div className="hz-strong" style={{ marginBottom: 4 }}>Possible duplicates</div>
                        {candidates.slice(0, 5).map((c) => (
                            <div key={c.id}>
                                <button type="button" className="hz-btn-link" style={{ fontSize: 13 }} onClick={() => open(c.id)}>{c.name}</button> <span className="hz-small">({humanize(c.matched_by)})</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
}
